// app/src/app/providers/TooltipProvider.jsx
import React, { useState, useContext, createContext, useEffect, useRef, useCallback } from 'react';

const TooltipContext = createContext();

export const useTooltip = () => {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error('useTooltip must be used within TooltipProvider');
  }
  return context;
};

export const TooltipProvider = ({ children }) => {
  const [tooltip, setTooltip] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const typingIntervalRef = useRef(null);
  const currentTextRef = useRef('');
  const currentIndexRef = useRef(0);

  const stopTypingAnimation = useCallback(() => {
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
    setIsTyping(false);
  }, []);

  const startTypingAnimation = useCallback((fullText) => {
    stopTypingAnimation();

    if (!fullText) {
      setDisplayText('');
      return;
    }

    currentTextRef.current = fullText;
    currentIndexRef.current = 0;
    setDisplayText('');
    setIsTyping(true);

    typingIntervalRef.current = setInterval(() => {
      if (currentTextRef.current !== fullText) {
        stopTypingAnimation();
        return;
      }

      if (currentIndexRef.current < fullText.length) {
        const nextChar = fullText[currentIndexRef.current];
        setDisplayText(prev => prev + nextChar);
        currentIndexRef.current++;
      } else {
        stopTypingAnimation();
      }
    }, 20);
  }, [stopTypingAnimation]);

  const showTooltip = useCallback((title, content, detail = null) => {
    if (!title || !content) return;

    setTooltip({ title, content, detail });
    setIsVisible(true);
    startTypingAnimation(content);
  }, [startTypingAnimation]);

  const hideTooltip = useCallback(() => {
    stopTypingAnimation();
    setIsVisible(false);
    setTooltip(null);
    setDisplayText('');
  }, [stopTypingAnimation]);

  const toggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  useEffect(() => {
    return () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
      }
    };
  }, []);

  return (
    <TooltipContext.Provider value={{ showTooltip, hideTooltip }}>
      {children}
      <TooltipBar
        tooltip={tooltip}
        isVisible={isVisible}
        isExpanded={isExpanded}
        isTyping={isTyping}
        displayText={displayText}
        onToggle={toggleExpand}
      />
    </TooltipContext.Provider>
  );
};

const TooltipBar = ({
  tooltip,
  isVisible,
  isExpanded,
  isTyping,
  displayText,
  onToggle
}) => {
  const handleToggle = (e) => {
    e.stopPropagation();
    onToggle();
  };

  return (
    <div
      className="retro-panel"
      style={{
        position: 'fixed',
        bottom: 'var(--spacing-xl)',
        right: 'var(--spacing-xl)',
        width: isExpanded ? '360px' : 'auto',
        zIndex: 1000000,
        transition: 'all var(--transition-base)',
        overflow: 'hidden',
        animation: 'retroSlideUp var(--transition-base) ease'
      }}
    >
      {isExpanded ? (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 'var(--spacing-md) var(--spacing-lg)',
              background: 'rgba(0,0,0,0.4)',
              borderBottom: '1px solid var(--retro-primary)',
              cursor: 'pointer'
            }}
            onClick={handleToggle}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-sm)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--retro-primary-light)',
              letterSpacing: '1px'
            }}>
              <span style={{
                animation: 'retroPulse 2s ease-in-out infinite'
              }}>📡</span>
              <span>ИНФО-ЦЕНТР</span>
            </div>
            <button
              className="retro-button"
              onClick={handleToggle}
              style={{
                padding: 'var(--spacing-xs) var(--spacing-sm)',
                fontSize: 'var(--font-size-xs)',
                lineHeight: 1,
                cursor: 'pointer'
              }}
            >
              _
            </button>
          </div>

          <div style={{
            padding: 'var(--spacing-lg)',
            minHeight: '120px',
            maxHeight: '300px',
            overflowY: 'auto'
          }}>
            {isVisible && tooltip ? (
              <div style={{
                animation: 'retroSlideUp var(--transition-base) ease'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-sm)',
                  marginBottom: 'var(--spacing-md)',
                  paddingBottom: 'var(--spacing-sm)',
                  borderBottom: '1px solid var(--retro-primary)'
                }}>
                  <span style={{
                    fontSize: 'var(--font-size-lg)',
                    animation: 'retroPulse 1s ease infinite'
                  }}>💡</span>
                  <span style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--retro-primary)',
                    fontWeight: 'bold'
                  }}>
                    {tooltip.title}
                  </span>
                </div>

                <div style={{
                  fontSize: 'var(--font-size-md)',
                  lineHeight: 1.6,
                  marginBottom: tooltip.detail ? 'var(--spacing-md)' : 0,
                  color: 'var(--retro-primary-light)'
                }}>
                  {displayText || tooltip.content}
                  {isTyping && (
                    <span style={{
                      display: 'inline-block',
                      animation: 'retroBlink 1s step-end infinite',
                      marginLeft: '2px'
                    }}>█</span>
                  )}
                </div>

                {tooltip.detail && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--spacing-sm)',
                    padding: 'var(--spacing-sm)',
                    marginTop: 'var(--spacing-md)',
                    background: 'rgba(0,0,0,0.3)',
                    borderLeft: `2px solid var(--retro-primary)`,
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--retro-secondary)'
                  }}>
                    <span>📌</span>
                    <span>{tooltip.detail}</span>
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: 'var(--spacing-md)'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 'var(--spacing-md)',
                  marginBottom: 'var(--spacing-md)',
                  fontSize: 'var(--font-size-2xl)'
                }}>
                  <span style={{ animation: 'retroPulse 2s ease-in-out infinite' }}>🎮</span>
                  <span style={{ animation: 'retroPulse 2s ease-in-out infinite 0.3s' }}>✨</span>
                  <span style={{ animation: 'retroPulse 2s ease-in-out infinite 0.6s' }}>⚡</span>
                </div>
                <p style={{
                  fontSize: 'var(--font-size-sm)',
                  marginBottom: 'var(--spacing-xs)',
                  color: 'var(--retro-primary-light)'
                }}>
                  Наведи на любой параметр
                </p>
                <span style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--retro-secondary)'
                }}>
                  для получения подробной информации
                </span>
              </div>
            )}
          </div>
        </>
      ) : (
        <button
          className="retro-button"
          onClick={handleToggle}
          style={{
            width: '48px',
            height: '48px',
            fontSize: 'var(--font-size-2xl)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <span style={{
            display: 'inline-block',
            animation: 'retroSpin 3s linear infinite'
          }}>?</span>
        </button>
      )}
    </div>
  );
};
